import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { onboardingAdmin as rules } from 'further-ui/validations/';
import { Column } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import { styled, Button, Box } from '@mui/material';
import TextField from '../StyledComponents/TextField';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';

const ButtonContainer = styled(Box)({
  textAlign: 'right',
});

type Props = {
  vehicleApplicationId: string;
  investorId: string;
  vehicleId: string;
  isActive: boolean;
  onSave: () => void;
};

const BankDetails: React.FC<Props> = ({
  investorId,
  isActive,
  vehicleId,
  vehicleApplicationId,
  onSave,
}) => {
  type FormValues = z.infer<typeof rules.SaveBankAccountDetailsInput>;

  const { bankDetails, saveBankAccountDetails, isPending } =
    useVehicleApplication(vehicleId, vehicleApplicationId);

  const defaultValues = useMemo(() => {
    return {
      investorId,
      bankName: bankDetails?.bankName ?? '',
      accountHolderName: bankDetails?.accountHolderName ?? '',
      bankAddress: bankDetails?.bankAddress ?? '',
      bankAccountNumber: bankDetails?.bankAccountNumber ?? '',
      additionalBankNumberOrCode: bankDetails?.additionalBankNumberOrCode ?? '',
      bankSortCode: bankDetails?.bankSortCode ?? '',
      iban: bankDetails?.iban ?? '',
      swift: bankDetails?.swift ?? '',
    };
  }, [bankDetails, investorId]);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(rules.SaveBankAccountDetailsInput),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [isActive, reset, defaultValues]);

  const onSubmit = (values: FormValues) => {
    if (isPending) {
      return;
    }

    saveBankAccountDetails(
      {
        vehicleId,
        ...values,
      },
      {
        onSuccess: onSave,
      },
    );
  };

  return (
    <Column fullWidth spacing="lg">
      <FormField label="Account beneficiary name">
        <Controller
          name="accountHolderName"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Account beneficiary name"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Bank name">
        <Controller
          name="bankName"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Bank name"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Bank address">
        <Controller
          name="bankAddress"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Bank address"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Bank account number">
        <Controller
          name="bankAccountNumber"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Bank account number"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Sort/ABA/ACH code">
        <Controller
          name="bankSortCode"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Sort/ABA/ACH code"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="SWIFT/BIC (if applicable)">
        <Controller
          name="swift"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="SWIFT/BIC (if applicable)"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="IBAN (if applicable)">
        <Controller
          name="iban"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="IBAN (if applicable)"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      <FormField label="Additional bank number or code (if applicable)">
        <Controller
          name="additionalBankNumberOrCode"
          disabled={!isActive}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Additional bank number or code (if applicable)"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FormField>
      {isActive ? (
        <ButtonContainer>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            size="large"
            disabled={isPending}
          >
            Save
          </Button>
        </ButtonContainer>
      ) : null}
    </Column>
  );
};

export default BankDetails;
